// Touch/pointer input + haptics for mobile-first games.
//
// Normalizes pointer events into tap / swipe / press gestures against a target element,
// with coordinates already mapped into the element's local space. Wraps navigator.vibrate
// (walkit's `buzz`) behind a safe helper so haptics never throw on unsupported devices.

export interface Point {
  x: number;
  y: number;
}

export type SwipeDir = 'up' | 'down' | 'left' | 'right';

export interface SwipeEvent extends Point {
  dir: SwipeDir;
  dx: number;
  dy: number;
}

export interface InputHandlers {
  /** Quick press+release without much movement. */
  onTap?: (p: Point) => void;
  /** Pointer down. */
  onPress?: (p: Point) => void;
  /** Pointer move while down. */
  onMove?: (p: Point) => void;
  /** Pointer up. */
  onRelease?: (p: Point) => void;
  /** A directional flick that exceeded the swipe threshold. */
  onSwipe?: (e: SwipeEvent) => void;
}

export interface InputOptions extends InputHandlers {
  /** Min distance (px) to count as a swipe. Default 30. */
  swipeThreshold?: number;
  /** Max distance (px) a tap may travel. Default 12. */
  tapSlop?: number;
}

export interface InputHandle {
  /** True while a pointer is currently down. */
  readonly down: boolean;
  /** Latest pointer position in element-local space. */
  readonly pos: Point;
  destroy(): void;
}

/** Fire a vibration pattern; silently no-ops where unsupported (walkit's `buzz`). */
export function haptic(pattern: number | number[]): void {
  try {
    if ('vibrate' in navigator) navigator.vibrate(pattern);
  } catch {
    /* unsupported — ignore */
  }
}

export function attachInput(el: HTMLElement, opts: InputOptions): InputHandle {
  const swipeThreshold = opts.swipeThreshold ?? 30;
  const tapSlop = opts.tapSlop ?? 12;

  let down = false;
  let startX = 0;
  let startY = 0;
  const pos: Point = { x: 0, y: 0 };

  const local = (e: PointerEvent): Point => {
    const r = el.getBoundingClientRect();
    return { x: e.clientX - r.left, y: e.clientY - r.top };
  };

  const onDown = (e: PointerEvent) => {
    down = true;
    const p = local(e);
    pos.x = p.x;
    pos.y = p.y;
    startX = p.x;
    startY = p.y;
    el.setPointerCapture?.(e.pointerId);
    opts.onPress?.(p);
  };

  const onMove = (e: PointerEvent) => {
    if (!down) return;
    const p = local(e);
    pos.x = p.x;
    pos.y = p.y;
    opts.onMove?.(p);
  };

  const onUp = (e: PointerEvent) => {
    if (!down) return;
    down = false;
    const p = local(e);
    pos.x = p.x;
    pos.y = p.y;
    opts.onRelease?.(p);

    const dx = p.x - startX;
    const dy = p.y - startY;
    const dist = Math.hypot(dx, dy);
    if (dist >= swipeThreshold && opts.onSwipe) {
      const dir: SwipeDir =
        Math.abs(dx) > Math.abs(dy) ? (dx > 0 ? 'right' : 'left') : dy > 0 ? 'down' : 'up';
      opts.onSwipe({ x: p.x, y: p.y, dx, dy, dir });
    } else if (dist <= tapSlop && opts.onTap) {
      opts.onTap(p);
    }
  };

  el.style.touchAction = 'none'; // stop the browser from stealing gestures (scroll/zoom)
  el.addEventListener('pointerdown', onDown);
  el.addEventListener('pointermove', onMove);
  el.addEventListener('pointerup', onUp);
  el.addEventListener('pointercancel', onUp);

  return {
    get down() {
      return down;
    },
    get pos() {
      return pos;
    },
    destroy() {
      el.removeEventListener('pointerdown', onDown);
      el.removeEventListener('pointermove', onMove);
      el.removeEventListener('pointerup', onUp);
      el.removeEventListener('pointercancel', onUp);
    },
  };
}
